import { useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { notificationsThunks } from '@/entity/notifications/index'
import { userSelectors } from '@/entity'
import { companySelectors } from '@/entity/company'
import { useAuth } from '@/processes/authorisePassport'
import { employerProfileSelectors } from '@features/employerProfile/model'

export const NotificationWrapper = ({ children }) => {
	const dispatch = useDispatch()
	const activeRole = useSelector(userSelectors.activeRole)
	const activeCompany = useSelector(employerProfileSelectors.activeCompany)
	const store = useStore()
	const { authData } = useAuth()

	// Храним предыдущее значение роли, чтобы реагировать на её изменение
	const prevRoleRef = useRef(activeRole)
	// Храним предыдущую активную компанию, чтобы реагировать на её смену
	const prevCompanyRef = useRef(activeCompany)
	// Время последней отправки запроса (для пропуска «старого» тика после смены роли)
	const lastDispatchTimeRef = useRef(0)

	useEffect(() => {
		if (authData.data) {
			// Инициализируем предыдущее значение роли
			prevRoleRef.current = activeRole
			// Инициализируем предыдущее значение компании
			prevCompanyRef.current = activeCompany

			// Подписка на стор: немедленно реагируем на смену роли
			const unsubscribe = store.subscribe(() => {
				const state = store.getState()
				const newActiveRole = userSelectors.activeRole(state)
				// Реагируем на смену активной компании
				const newActiveCompany = employerProfileSelectors.activeCompany(state)
				const companyChanged = newActiveCompany?.id !== prevCompanyRef.current?.id
				if (newActiveRole !== prevRoleRef.current || companyChanged) {
					const companiesLen = companySelectors.companies(state)?.length || 0
					const userIinLen = userSelectors.user(state)?.iin?.length || 0

					const isEmployer = companiesLen > 0 && newActiveRole === 'ROLE_EMPLOYER'
					const isEmployee = Boolean(userIinLen) && newActiveRole === 'ROLE_EMPLOYEE'

					if (isEmployer || isEmployee) {
						// Немедленный запрос с новой ролью
						lastDispatchTimeRef.current = Date.now()
						dispatch(notificationsThunks.getNotificationsBadge(newActiveRole === 'ROLE_EMPLOYER'))
					}

					prevRoleRef.current = newActiveRole
					prevCompanyRef.current = newActiveCompany
				}
			})

			// Стабильный интервал раз в минуту
			const tick = () => {
				const state = store.getState()
				const currentRole = userSelectors.activeRole(state)
				const companiesLen = companySelectors.companies(state)?.length || 0
				const userIinLen = userSelectors.user(state)?.iin?.length || 0

				const isEmployer = companiesLen > 0 && currentRole === 'ROLE_EMPLOYER'
				const isEmployee = Boolean(userIinLen) && currentRole === 'ROLE_EMPLOYEE'

				// Не чаще одного раза в минуту
				const now = Date.now()
				if (isEmployer || isEmployee) {
					if (now - lastDispatchTimeRef.current >= 60000) {
						lastDispatchTimeRef.current = now
						dispatch(notificationsThunks.getNotificationsBadge(currentRole === 'ROLE_EMPLOYER'))
					}
				}
			}

			const intervalId = setInterval(tick, 60000)

			return () => {
				clearInterval(intervalId)
				unsubscribe()
			}
		}
	}, [authData.data])

	return children
}
